<template>
    <div>
        <h2 class="clickable" :class="{'collapsed-heading': !expanded}" @click="expanded=!expanded">
            Brake Check Configuration
            <v-icon v-if="expanded">arrow_drop_up</v-icon>
            <v-icon v-else>arrow_drop_down</v-icon>
        </h2>
        <v-wait for="loading brake check">
            <v-expand-transition>
                <v-card v-show="expanded">
                    <v-container fluid>
                        <v-layout row wrap>
                            <v-flex md6>
                                <v-select
                                    label="Brake Type"
                                    :items="brakeTypes"
                                    v-model="brakeCheck.brakeType"
                                    v-validate="'required'"
                                    :error-messages="errors.first('brakeType')"
                                    name="brakeType"
                                    required
                                    item-value="key"
                                    item-text="value"
                                    :disabled="this.unit.isDeleted || !userIsFleetManager"
                                    @change="brakeTypeChanged"
                                ></v-select>
                            </v-flex>
                            <v-flex md6>
                                <v-select
                                    label="Adjustor Type"
                                    :items="slackTypes"
                                    v-model="brakeCheck.slackType"
                                    v-validate="'required'"
                                    :error-messages="errors.first('slackType')"
                                    name="slackType"
                                    required
                                    :disabled="this.unit.isDeleted || !userIsFleetManager || brakeCheck.brakeType == 'Disc'"
                                ></v-select>
                            </v-flex>
                            <v-flex md6>
                                <v-select
                                    label="Exemption Type"
                                    ref="exemptionType"
                                    :items="exemptionTypes"
                                    v-model="brakeCheck.exemptionType"
                                    v-validate="'required'"
                                    :error-messages="errors.first('exemptionType')"
                                    name="exemptionType"
                                    required
                                    item-value="key"
                                    item-text="value"
                                    :disabled="this.unit.isDeleted || !userIsFleetManager"
                                ></v-select>
                            </v-flex>
                            <v-flex md6>
                                <v-select
                                    label="Service Configuration"
                                    :items="brakeConfigurations"
                                    v-model="brakeCheck.brakeCheckConfigurationId"
                                    :error-messages="errors.first('brakeCheckConfigurationId')"
                                    v-validate="'required_if:exemptionType,ExemptScheduled'"
                                    name="brakeCheckConfigurationId"
                                    item-value="id"
                                    item-text="name"
                                    :disabled="this.unit.isDeleted || !userIsFleetManager || brakeCheck.exemptionType != 'ExemptScheduled'"
                                ></v-select>
                            </v-flex>
                            <v-flex md6>
                                <v-select
                                    label="Schedule"
                                    :items="schedules"
                                    :clearable="isNew"
                                    v-model="brakeCheck.brakeCheckSchedule"
                                    :error-messages="errors.first('brakeCheckSchedule')"
                                    name="brakeCheckSchedule"
                                    item-value="key"
                                    item-text="value"
                                    :disabled="this.unit.isDeleted || brakeCheck.exemptionType != 'NotExempt'"
                                ></v-select>
                            </v-flex>
                        </v-layout>
                    </v-container>
                    <v-card-actions v-if="!disabled">
                        <v-spacer></v-spacer>
                        <v-btn color="primary" :disabled="disableSubmit" @click="submit">Save</v-btn>
                    </v-card-actions>
                </v-card>
            </v-expand-transition>
        </v-wait>
    </div>
</template>

<script>
  import {ConfigurationType} from '@/models/enums';
  import Configuration from '@/components/Configurations/ConfigurationsClient.vue';
  import BrakeCheckClient from '@/components/BrakeChecks/BrakeCheckClient.vue';
  import FormValidation from '@/components/FormValidation.vue';
  import {GarageLocationStore} from '@/stores/GarageLocationStore';

  export default {
    mixins: [BrakeCheckClient, FormValidation, Configuration],
    props: {
      unit: {required: true}
    },
    components: {},
    data() {
      return {
        expanded: false,
        brakeCheck: {},
        brakeTypes: [{key: 'Disc', value: 'Disc'}, {key: 'Drum', value: 'Drum'}, {key: 'DiscAndDrum', value: 'Disc and Drum'}],
        exemptionTypes: [{key: 'NotExempt', value: 'Not Exempt'}, {key: 'ExemptScheduled', value: 'Exempt - Scheduled'}, {key: 'ExemptNotRequired', value: 'Exempt - Not Required'}],
        schedules: [{key: 'Week1', value: 'Week 1'}, {key: 'Week2', value: 'Week 2'}, {key: 'Both', value: 'Both'}],
        slackTypes: ['Auto', 'Manual'],
        isNew: true,
        configurations: []
      };
    },
    computed: {
      unitId() {
        return this.$route.params.unitId;
      },
      disableSubmit() {
        return false;
      },
      brakeConfigurations() {
        return this.configurations.filter(c => c.type === ConfigurationType.Brake);
      },
      disabled() {
        return this.unit.isDeleted || !this.userIsFleetOrWorkshopManager;
      }
    },
    methods: {
      async getBrakeCheckConfig() {
        const config = await this.getUnitBrakeCheck(this.unitId);
        this.brakeCheck = config || {
          id: this.$uuid.v1(),
          unitId: this.unitId
        };
      },
      async submitForm() {
        if (this.userIsFleetManager) {
          await this.updateBrakeCheck(this.brakeCheck);
        } else {
          await this.setBrakeCheckSchedule(this.brakeCheck);
        }
        this.getBrakeCheckConfig();
      },
      brakeTypeChanged() {
        if (this.brakeCheck.brakeType == 'Disc') {
          this.brakeCheck.slackType = 'Auto';
        }
      }
    },
    async mounted() {
      await this.getBrakeCheckConfig();
      this.configurations = await this.getConfigurations();

      this.$validator.localize({
        en: {
          custom: {
            scheduleIntervalType: {
              required_if: `This is required for Exemption Type 'Scheduled'`
            }
          }
        }
      });
    },
    watch: {
      async unitId(val) {
        this.getBrakeCheckConfig();
      }
    }
  };
</script>
<style>
    td {
        cursor: pointer;
    }
</style>
